import { generateUUID } from "../../shared/services/utils";

// We keep everything flat because it's easier to manage form state (vs nesting)
export interface Bq1GeneralInformationM {
  balticFormSubmitted: string | null;
  lrImoNumber: string;
  lastPreviousName: string;
  lastPreviousNameComments: string;
  lastPreviousNameDateChange: string | null;
  secondLastPreviousName: string;
  secondLastPreviousNameDateChange: string | null;
  thirdLastPreviousName: string;
  thirdLastPreviousNameDateChange: string | null;
  fourthLastPreviousName: string;
  fourthLastPreviousNameDateChange: string | null;
  flag: string;
  portOfRegistry: string;
  portOfRegistryComments: string;
  shipType: string;
  typeOfShipIfOther: string;
  typeOfHull: string;

  // Communications and Electronics
  callSign: string;
  vesselTelephoneInmarsat: string;
  vesselTelexNumber: string;
  vesselFaxNumber: string;
  vesselEmailAddress: string;
  vesselMmsiNo: string;
  vesselElectricalSupply: string;

  // Ownership and Operation
  registeredOwnerName: string;
  registeredOwnerFullAddress: string;
  registeredOwnerCountry: string;
  registeredOwnerOfficeTel: string;
  registeredOwnerOfficeFax: string;
  registeredOwnerOfficeTelex: string;
  registeredOwnerOfficeEmail: string;
  registeredOwnerWebsite: string;
  parentCompanyGroup: string;
  parentCompanyAddress: string;
  parentCompanyTel: string;
  parentCompanyFax: string;
  parentCompanyTelex: string;
  parentCompanyEmail: string;
  parentCompanyWeb: string;
  technicalOperatorName: string;
  technicalOperatorFullAddress: string;
  technicalOperatorCountry: string;
  technicalOperatorOfficeTel: string;
  technicalOperatorOfficeFax: string;
  technicalOperatorOfficeTelex: string;
  technicalOperatorOfficeEmail: string;
  technicalOperatorWebsite: string;
  technicalOperatorCompanyImoNumber: string;
  commercialOperatorName: string;
  commercialOperatorFullAddress: string;
  commercialOperatorCountry: string;
  commercialOperatorOfficeTel: string;
  commercialOperatorOfficeFax: string;
  commercialOperatorOfficeTelex: string;
  commercialOperatorOfficeEmail: string;
  commercialOperatorWebsite: string;
  disponentOwnerName: string;
  disponentOwnerFullAddress: string;
  disponentOwnerTel: string;
  disponentOwnerFax: string;
  disponentOwnerTelex: string;
  disponentOwnerEmail: string;
  disponentOwnerWebsite: string;
  isVesselOwnedOrCharter: string;
  disponentOwnerSince: string;
  numberOfVesselsInDisponentFleet: string;

  // Builder
  builderName: string;
  yardNumber: string;
  deliveryDateRecorded: string | null;

  // Classification
  classificationSociety: string;
  classNotation: string;
  previousClassificationSociety: string;
  dateClassificationSocietyChanged: string | null;
  dateOfLastDryDock: string | null;
  placeOfLastDryDock: string;
  dateNextDryDockDue: string | null;
  dateOfLastSpecialSurvey: string | null;
  dateNextSpecialSurveyDue: string | null;
  certificateOfClassLastAnnual: string | null;
  certificateOfClassNextAnnual: string | null;
  doesVesselHaveEsp: string;
  doesVesselHaveEspComments: string;
  doesVesselComplyWithIacsNo1Cargo: string;
  hasClassSocietyVerifiedIacsNo1Cargo: string;
  hasClassSocietyVerifiedIacsNo1CargoComments: string;

  // Dimensions
  lengthOverallLoa: string;
  lengthOverallLoaComments: string;
  lengthBetweenPerp: string;
  lengthBetweenPerpComments: string;
  beamExtreme: string;
  beamExtremeComments: string;
  mouldedDepth: string;
  mouldedDepthComments: string;
  keelToMastheadKtm: string;
  keelToMastheadKtmComments: string;
  keelToMastheadKtmCollapsed: string;
  keelToMastheadKtmCollapsedComments: string;
  wlDistanceBallastNo1: string;
  wlDistanceBallastNo1Comments: string;
  wlDistanceBallastMidships: string;
  wlDistanceBallastMidshipsComments: string;
  wlDistanceBallastLast: string;
  wlDistanceBallastLastComments: string;
  wlDistanceFullBallastNo1: string;
  wlDistanceFullBallastNo1Comments: string;
  wlDistanceFullBallastMidships: string;
  wlDistanceFullBallastMidshipsComments: string;
  wlDistanceFullBallastLast: string;
  wlDistanceFullBallastLastComments: string;
  wlDistanceLightNo1: string;
  wlDistanceLightNo1Comments: string;
  wlDistanceLightMidships: string;
  wlDistanceLightMidshipsComments: string;
  wlDistanceLightLast: string;
  wlDistanceLightLastComments: string;
  wlDistanceLadenNo1: string;
  wlDistanceLadenNo1Comments: string;
  wlDistanceLadenMidships: string;
  wlDistanceLadenMidshipsComments: string;
  wlDistanceLadenLast: string;
  wlDistanceLadenLastComments: string;
  distKeelNo1: string;
  distKeelNo1Comments: string;
  distKeelMidships: string;
  distKeelMidshipsComments: string;
  distKeelLast: string;
  distKeelLastComments: string;
  hatchesSingleOrDoubleSeals: string;

  // Tonnages
  grossTonnageGRT: string;
  netRegisteredTonnageNRT: string;
  suezCanalGrossTonnageSCGT: string;
  suezCanalGrossTonnageSCGTComments: string;
  suezCanalNetTonnageSCNT: string;
  suezCanalNetTonnageSCNTComments: string;
  panamaCanalNetTonnagePCNT: string;

  // Loadline info
  summerDeadweight: string;
  summerDraft: string;
  tpcAtSummerDraft: string;
  winterDeadweight: string;
  winterDraft: string;
  tpcImmersionWinterDraft: string;
  winterNorthAtlanticDeadweight: string;
  winterNorthAtlanticDraft: string;
  tpcImmersionWinterNorthAtlanticDraft: string;
  freshWaterDeadweight: string;
  freshWaterDraft: string;
  tpcImmersionFreshWaterDraft: string;
  tropicalDeadweight: string;
  tropicalDraft: string;
  tpcImmersionTropicalDraft: string;
  tropicalFreshWaterDeadweight: string;
  tropicalFreshWaterDraft: string;
  tpcImmersionTropicalFreshDraft: string;
  normalBallastConditionDeadweight: string;
  normalBallastConditionDraft: string;
  normalBallastConditionDraftComments: string;
  tpcImmersionNormalBallastDraft: string;
  lightshipDeadweight: string;
  lightshipDraft: string;
  tpcImmersionLightshipDraft: string;
  lakesDeadweight: string;
  lakesDraft: string;
  tpcImmersionLakesDraft: string;
  freshWaterAllowanceAtSummerDraft: string;

  // Clearence
  underKeelClearanceGuidelines: string;
  underKeelClearanceGuidelinesComments: string;

  // Panama
  fittedForPanama: string;
  fittedForPanamaComments: string;
  panamaDeadweightAllTold: string;
  isPanamaDwAffectedBilgeTurnRadius: string;
  isPanamaDwAffectedBilgeTurnRadiusComments: string;
  fittedForSuez: string;
  fittedForSuezComments: string;
  fittedForStLawrence: string;
  fittedForStLawrenceComments: string;
  stLawrenceDeadweightAllTold: string;
  stLawrenceDeadweightAllToldComments: string;

  // Pollution
  pollutionGroundingCollision12Months: string;
  detailsIfIncidentPast12Months: IncidentDetailsM[];

  // Misc
  useAnswerFromCargoHistory: string;
  lastThreeVoyages: string;
  internationalShipSecurityCertificate: string;
}

export interface IncidentDetailsM {
  id: string;
  incidentId: string;
  date: string | null;
  type: string;
  location: string;
}

export const initialBq1GeneralInformation: Bq1GeneralInformationM = {
  balticFormSubmitted: null,
  lrImoNumber: "",
  lastPreviousName: "",
  lastPreviousNameComments: "",
  lastPreviousNameDateChange: null,
  secondLastPreviousName: "",
  secondLastPreviousNameDateChange: null,
  thirdLastPreviousName: "",
  thirdLastPreviousNameDateChange: null,
  fourthLastPreviousName: "",
  fourthLastPreviousNameDateChange: null,
  flag: "",
  portOfRegistry: "",
  portOfRegistryComments: "",
  shipType: "",
  typeOfShipIfOther: "",
  typeOfHull: "",
  callSign: "",
  vesselTelephoneInmarsat: "",
  vesselTelexNumber: "",
  vesselFaxNumber: "",
  vesselEmailAddress: "",
  vesselMmsiNo: "",
  vesselElectricalSupply: "",
  registeredOwnerName: "",
  registeredOwnerFullAddress: "",
  registeredOwnerCountry: "",
  registeredOwnerOfficeTel: "",
  registeredOwnerOfficeFax: "",
  registeredOwnerOfficeTelex: "",
  registeredOwnerOfficeEmail: "",
  registeredOwnerWebsite: "",
  parentCompanyGroup: "",
  parentCompanyAddress: "",
  parentCompanyTel: "",
  parentCompanyFax: "",
  parentCompanyTelex: "",
  parentCompanyEmail: "",
  parentCompanyWeb: "",
  technicalOperatorName: "",
  technicalOperatorFullAddress: "",
  technicalOperatorCountry: "",
  technicalOperatorOfficeTel: "",
  technicalOperatorOfficeFax: "",
  technicalOperatorOfficeTelex: "",
  technicalOperatorOfficeEmail: "",
  technicalOperatorWebsite: "",
  technicalOperatorCompanyImoNumber: "",
  commercialOperatorName: "",
  commercialOperatorFullAddress: "",
  commercialOperatorCountry: "",
  commercialOperatorOfficeTel: "",
  commercialOperatorOfficeFax: "",
  commercialOperatorOfficeTelex: "",
  commercialOperatorOfficeEmail: "",
  commercialOperatorWebsite: "",
  disponentOwnerName: "",
  disponentOwnerFullAddress: "",
  disponentOwnerTel: "",
  disponentOwnerFax: "",
  disponentOwnerTelex: "",
  disponentOwnerEmail: "",
  disponentOwnerWebsite: "",
  isVesselOwnedOrCharter: "",
  disponentOwnerSince: "",
  numberOfVesselsInDisponentFleet: "",
  builderName: "",
  yardNumber: "",
  deliveryDateRecorded: null,
  classificationSociety: "",
  classNotation: "",
  previousClassificationSociety: "",
  dateClassificationSocietyChanged: null,
  dateOfLastDryDock: null,
  placeOfLastDryDock: "",
  dateNextDryDockDue: null,
  dateOfLastSpecialSurvey: null,
  dateNextSpecialSurveyDue: null,
  certificateOfClassLastAnnual: null,
  certificateOfClassNextAnnual: null,
  doesVesselHaveEsp: "",
  doesVesselHaveEspComments: "",
  doesVesselComplyWithIacsNo1Cargo: "",
  hasClassSocietyVerifiedIacsNo1Cargo: "",
  hasClassSocietyVerifiedIacsNo1CargoComments: "",
  lengthOverallLoa: "",
  lengthOverallLoaComments: "",
  lengthBetweenPerp: "",
  lengthBetweenPerpComments: "",
  beamExtreme: "",
  beamExtremeComments: "",
  mouldedDepth: "",
  mouldedDepthComments: "",
  keelToMastheadKtm: "",
  keelToMastheadKtmComments: "",
  keelToMastheadKtmCollapsed: "",
  keelToMastheadKtmCollapsedComments: "",
  wlDistanceBallastNo1: "",
  wlDistanceBallastNo1Comments: "",
  wlDistanceBallastMidships: "",
  wlDistanceBallastMidshipsComments: "",
  wlDistanceBallastLast: "",
  wlDistanceBallastLastComments: "",
  wlDistanceFullBallastNo1: "",
  wlDistanceFullBallastNo1Comments: "",
  wlDistanceFullBallastMidships: "",
  wlDistanceFullBallastMidshipsComments: "",
  wlDistanceFullBallastLast: "",
  wlDistanceFullBallastLastComments: "",
  wlDistanceLightNo1: "",
  wlDistanceLightNo1Comments: "",
  wlDistanceLightMidships: "",
  wlDistanceLightMidshipsComments: "",
  wlDistanceLightLast: "",
  wlDistanceLightLastComments: "",
  wlDistanceLadenNo1: "",
  wlDistanceLadenNo1Comments: "",
  wlDistanceLadenMidships: "",
  wlDistanceLadenMidshipsComments: "",
  wlDistanceLadenLast: "",
  wlDistanceLadenLastComments: "",
  distKeelNo1: "",
  distKeelNo1Comments: "",
  distKeelMidships: "",
  distKeelMidshipsComments: "",
  distKeelLast: "",
  distKeelLastComments: "",
  hatchesSingleOrDoubleSeals: "",
  grossTonnageGRT: "",
  netRegisteredTonnageNRT: "",
  suezCanalGrossTonnageSCGT: "",
  suezCanalGrossTonnageSCGTComments: "",
  suezCanalNetTonnageSCNT: "",
  suezCanalNetTonnageSCNTComments: "",
  panamaCanalNetTonnagePCNT: "",
  summerDeadweight: "",
  summerDraft: "",
  tpcAtSummerDraft: "",
  winterDeadweight: "",
  winterDraft: "",
  tpcImmersionWinterDraft: "",
  winterNorthAtlanticDeadweight: "",
  winterNorthAtlanticDraft: "",
  tpcImmersionWinterNorthAtlanticDraft: "",
  freshWaterDeadweight: "",
  freshWaterDraft: "",
  tpcImmersionFreshWaterDraft: "",
  tropicalDeadweight: "",
  tropicalDraft: "",
  tpcImmersionTropicalDraft: "",
  tropicalFreshWaterDeadweight: "",
  tropicalFreshWaterDraft: "",
  tpcImmersionTropicalFreshDraft: "",
  normalBallastConditionDeadweight: "",
  normalBallastConditionDraft: "",
  normalBallastConditionDraftComments: "",
  tpcImmersionNormalBallastDraft: "",
  lightshipDeadweight: "",
  lightshipDraft: "",
  tpcImmersionLightshipDraft: "",
  lakesDeadweight: "",
  lakesDraft: "",
  tpcImmersionLakesDraft: "",
  freshWaterAllowanceAtSummerDraft: "",
  underKeelClearanceGuidelines: "",
  underKeelClearanceGuidelinesComments: "",
  fittedForPanama: "",
  fittedForPanamaComments: "",
  panamaDeadweightAllTold: "",
  isPanamaDwAffectedBilgeTurnRadius: "",
  isPanamaDwAffectedBilgeTurnRadiusComments: "",
  fittedForSuez: "",
  fittedForSuezComments: "",
  fittedForStLawrence: "",
  fittedForStLawrenceComments: "",
  stLawrenceDeadweightAllTold: "",
  stLawrenceDeadweightAllToldComments: "",
  pollutionGroundingCollision12Months: "",
  detailsIfIncidentPast12Months: [
    {
      id: "",
      incidentId: generateUUID(),
      date: null,
      type: "",
      location: "",
    },
  ],
  useAnswerFromCargoHistory: "",
  lastThreeVoyages: "",
  internationalShipSecurityCertificate: "",
};

export function mapSqlToBq1GenInfoForm(genInfo: any, incidents: any[]): Bq1GeneralInformationM {
  return {
    balticFormSubmitted: genInfo?.BALTIC_FORM_SUBMITTED || null,
    lrImoNumber: genInfo?.LR_IMO_NUMBER || "",
    lastPreviousName: genInfo?.LAST_PREVIOUS_NAME || "",
    lastPreviousNameComments: genInfo?.LAST_PREVIOUS_NAME_COMMENTS || "",
    lastPreviousNameDateChange: genInfo?.LAST_PREVIOUS_NAME_DATE_CHANGE || null,
    secondLastPreviousName: genInfo?.SECOND_LAST_PREVIOUS_NAME || "",
    secondLastPreviousNameDateChange: genInfo?.SECOND_LAST_PREVIOUS_NAME_DATE_CHANGE || null,
    thirdLastPreviousName: genInfo?.THIRD_LAST_PREVIOUS_NAME || "",
    thirdLastPreviousNameDateChange: genInfo?.THIRD_LAST_PREVIOUS_NAME_DATE_CHANGE || null,
    fourthLastPreviousName: genInfo?.FOURTH_LAST_PREVIOUS_NAME || "",
    fourthLastPreviousNameDateChange: genInfo?.FOURTH_LAST_PREVIOUS_NAME_DATE_CHANGE || null,
    flag: genInfo?.FLAG || "",
    portOfRegistry: genInfo?.PORT_OF_REGISTRY || "",
    portOfRegistryComments: genInfo?.PORT_OF_REGISTRY_COMMENTS || "",
    shipType: genInfo?.SHIP_TYPE || "",
    typeOfShipIfOther: genInfo?.TYPE_OF_SHIP_IF_OTHER || "",
    typeOfHull: genInfo?.TYPE_OF_HULL || "",
    callSign: genInfo?.CALL_SIGN || "",
    vesselTelephoneInmarsat: genInfo?.VESSEL_TELEPHONE_INMARSAT || "",
    vesselTelexNumber: genInfo?.VESSEL_TELEX_NUMBER || "",
    vesselFaxNumber: genInfo?.VESSEL_FAX_NUMBER || "",
    vesselEmailAddress: genInfo?.VESSEL_EMAIL_ADDRESS || "",
    vesselMmsiNo: genInfo?.VESSEL_MMSI_NO || "",
    vesselElectricalSupply: genInfo?.VESSEL_ELECTRICAL_SUPPLY || "",
    registeredOwnerName: genInfo?.REGISTERED_OWNER_NAME || "",
    registeredOwnerFullAddress: genInfo?.REGISTERED_OWNER_FULL_ADDRESS || "",
    registeredOwnerCountry: genInfo?.REGISTERED_OWNER_COUNTRY || "",
    registeredOwnerOfficeTel: genInfo?.REGISTERED_OWNER_OFFICE_TEL || "",
    registeredOwnerOfficeFax: genInfo?.REGISTERED_OWNER_OFFICE_FAX || "",
    registeredOwnerOfficeTelex: genInfo?.REGISTERED_OWNER_OFFICE_TELEX || "",
    registeredOwnerOfficeEmail: genInfo?.REGISTERED_OWNER_OFFICE_EMAIL || "",
    registeredOwnerWebsite: genInfo?.REGISTERED_OWNER_WEBSITE || "",
    parentCompanyGroup: genInfo?.PARENT_COMPANY_GROUP || "",
    parentCompanyAddress: genInfo?.PARENT_COMPANY_ADDRESS || "",
    parentCompanyTel: genInfo?.PARENT_COMPANY_TEL || "",
    parentCompanyFax: genInfo?.PARENT_COMPANY_FAX || "",
    parentCompanyTelex: genInfo?.PARENT_COMPANY_TELEX || "",
    parentCompanyEmail: genInfo?.PARENT_COMPANY_EMAIL || "",
    parentCompanyWeb: genInfo?.PARENT_COMPANY_WEB || "",
    technicalOperatorName: genInfo?.TECHNICAL_OPERATOR_NAME || "",
    technicalOperatorFullAddress: genInfo?.TECHNICAL_OPERATOR_FULL_ADDRESS || "",
    technicalOperatorCountry: genInfo?.TECHNICAL_OPERATOR_COUNTRY || "",
    technicalOperatorOfficeTel: genInfo?.TECHNICAL_OPERATOR_OFFICE_TEL || "",
    technicalOperatorOfficeFax: genInfo?.TECHNICAL_OPERATOR_OFFICE_FAX || "",
    technicalOperatorOfficeTelex: genInfo?.TECHNICAL_OPERATOR_OFFICE_TELEX || "",
    technicalOperatorOfficeEmail: genInfo?.TECHNICAL_OPERATOR_OFFICE_EMAIL || "",
    technicalOperatorWebsite: genInfo?.TECHNICAL_OPERATOR_WEBSITE || "",
    technicalOperatorCompanyImoNumber: genInfo?.TECHNICAL_OPERATOR_COMPANY_IMO_NUMBER || "",
    commercialOperatorName: genInfo?.COMMERCIAL_OPERATOR_NAME || "",
    commercialOperatorFullAddress: genInfo?.COMMERCIAL_OPERATOR_FULL_ADDRESS || "",
    commercialOperatorCountry: genInfo?.COMMERCIAL_OPERATOR_COUNTRY || "",
    commercialOperatorOfficeTel: genInfo?.COMMERCIAL_OPERATOR_OFFICE_TEL || "",
    commercialOperatorOfficeFax: genInfo?.COMMERCIAL_OPERATOR_OFFICE_FAX || "",
    commercialOperatorOfficeTelex: genInfo?.COMMERCIAL_OPERATOR_OFFICE_TELEX || "",
    commercialOperatorOfficeEmail: genInfo?.COMMERCIAL_OPERATOR_OFFICE_EMAIL || "",
    commercialOperatorWebsite: genInfo?.COMMERCIAL_OPERATOR_WEBSITE || "",
    disponentOwnerName: genInfo?.DISPONENT_OWNER_NAME || "",
    disponentOwnerFullAddress: genInfo?.DISPONENT_OWNER_FULL_ADDRESS || "",
    disponentOwnerTel: genInfo?.DISPONENT_OWNER_TEL || "",
    disponentOwnerFax: genInfo?.DISPONENT_OWNER_FAX || "",
    disponentOwnerTelex: genInfo?.DISPONENT_OWNER_TELEX || "",
    disponentOwnerEmail: genInfo?.DISPONENT_OWNER_EMAIL || "",
    disponentOwnerWebsite: genInfo?.DISPONENT_OWNER_WEBSITE || "",
    isVesselOwnedOrCharter: genInfo?.IS_VESSEL_OWNED_OR_CHARTER || "",
    disponentOwnerSince: genInfo?.DISPONENT_OWNER_SINCE || "",
    numberOfVesselsInDisponentFleet: genInfo?.NUMBER_OF_VESSELS_IN_DISPONENT_FLEET || "",
    builderName: genInfo?.BUILDER_NAME || "",
    yardNumber: genInfo?.YARD_NUMBER || "",
    deliveryDateRecorded: genInfo?.DELIVERY_DATE_RECORDED || null,
    classificationSociety: genInfo?.CLASSIFICATION_SOCIETY || "",
    classNotation: genInfo?.CLASS_NOTATION || "",
    previousClassificationSociety: genInfo?.PREVIOUS_CLASSIFICATION_SOCIETY || "",
    dateClassificationSocietyChanged: genInfo?.DATE_CLASSIFICATION_SOCIETY_CHANGED || null,
    dateOfLastDryDock: genInfo?.DATE_OF_LAST_DRY_DOCK || null,
    placeOfLastDryDock: genInfo?.PLACE_OF_LAST_DRY_DOCK || "",
    dateNextDryDockDue: genInfo?.DATE_NEXT_DRY_DOCK_DUE || null,
    dateOfLastSpecialSurvey: genInfo?.DATE_OF_LAST_SPECIAL_SURVEY || null,
    dateNextSpecialSurveyDue: genInfo?.DATE_NEXT_SPECIAL_SURVEY_DUE || null,
    certificateOfClassLastAnnual: genInfo?.CERTIFICATE_OF_CLASS_LAST_ANNUAL || null,
    certificateOfClassNextAnnual: genInfo?.CERTIFICATE_OF_CLASS_NEXT_ANNUAL || null,
    doesVesselHaveEsp: genInfo?.DOES_VESSEL_HAVE_ESP || "",
    doesVesselHaveEspComments: genInfo?.DOES_VESSEL_HAVE_ESP_COMMENTS || "",
    doesVesselComplyWithIacsNo1Cargo: genInfo?.DOES_VESSEL_COMPLY_WITH_IACS_NO1_CARGO || "",
    hasClassSocietyVerifiedIacsNo1Cargo: genInfo?.HAS_CLASS_SOCIETY_VERIFIED_IACS_NO1_CARGO || "",
    hasClassSocietyVerifiedIacsNo1CargoComments: genInfo?.HAS_CLASS_SOCIETY_VERIFIED_IACS_NO1_CARGO_COMMENTS || "",
    lengthOverallLoa: genInfo?.LENGTH_OVERALL_LOA || "",
    lengthOverallLoaComments: genInfo?.LENGTH_OVERALL_LOA_COMMENTS || "",
    lengthBetweenPerp: genInfo?.LENGTH_BETWEEN_PERP || "",
    lengthBetweenPerpComments: genInfo?.LENGTH_BETWEEN_PERP_COMMENTS || "",
    beamExtreme: genInfo?.BEAM_EXTREME || "",
    beamExtremeComments: genInfo?.BEAM_EXTREME_COMMENTS || "",
    mouldedDepth: genInfo?.MOULDED_DEPTH || "",
    mouldedDepthComments: genInfo?.MOULDED_DEPTH_COMMENTS || "",
    keelToMastheadKtm: genInfo?.KEEL_TO_MASTHEAD_KTM || "",
    keelToMastheadKtmComments: genInfo?.KEEL_TO_MASTHEAD_KTM_COMMENTS || "",
    keelToMastheadKtmCollapsed: genInfo?.KEEL_TO_MASTHEAD_KTM_COLLAPSED || "",
    keelToMastheadKtmCollapsedComments: genInfo?.KEEL_TO_MASTHEAD_KTM_COLLAPSED_COMMENTS || "",
    wlDistanceBallastNo1: genInfo?.WL_DISTANCE_BALLAST_NO1 || "",
    wlDistanceBallastNo1Comments: genInfo?.WL_DISTANCE_BALLAST_NO1_COMMENTS || "",
    wlDistanceBallastMidships: genInfo?.WL_DISTANCE_BALLAST_MIDSHIPS || "",
    wlDistanceBallastMidshipsComments: genInfo?.WL_DISTANCE_BALLAST_MIDSHIPS_COMMENTS || "",
    wlDistanceBallastLast: genInfo?.WL_DISTANCE_BALLAST_LAST || "",
    wlDistanceBallastLastComments: genInfo?.WL_DISTANCE_BALLAST_LAST_COMMENTS || "",
    wlDistanceFullBallastNo1: genInfo?.WL_DISTANCE_FULL_BALLAST_NO1 || "",
    wlDistanceFullBallastNo1Comments: genInfo?.WL_DISTANCE_FULL_BALLAST_NO1_COMMENTS || "",
    wlDistanceFullBallastMidships: genInfo?.WL_DISTANCE_FULL_BALLAST_MIDSHIPS || "",
    wlDistanceFullBallastMidshipsComments: genInfo?.WL_DISTANCE_FULL_BALLAST_MIDSHIPS_COMMENTS || "",
    wlDistanceFullBallastLast: genInfo?.WL_DISTANCE_FULL_BALLAST_LAST || "",
    wlDistanceFullBallastLastComments: genInfo?.WL_DISTANCE_FULL_BALLAST_LAST_COMMENTS || "",
    wlDistanceLightNo1: genInfo?.WL_DISTANCE_LIGHT_NO1 || "",
    wlDistanceLightNo1Comments: genInfo?.WL_DISTANCE_LIGHT_NO1_COMMENTS || "",
    wlDistanceLightMidships: genInfo?.WL_DISTANCE_LIGHT_MIDSHIPS || "",
    wlDistanceLightMidshipsComments: genInfo?.WL_DISTANCE_LIGHT_MIDSHIPS_COMMENTS || "",
    wlDistanceLightLast: genInfo?.WL_DISTANCE_LIGHT_LAST || "",
    wlDistanceLightLastComments: genInfo?.WL_DISTANCE_LIGHT_LAST_COMMENTS || "",
    wlDistanceLadenNo1: genInfo?.WL_DISTANCE_LADEN_NO1 || "",
    wlDistanceLadenNo1Comments: genInfo?.WL_DISTANCE_LADEN_NO1_COMMENTS || "",
    wlDistanceLadenMidships: genInfo?.WL_DISTANCE_LADEN_MIDSHIPS || "",
    wlDistanceLadenMidshipsComments: genInfo?.WL_DISTANCE_LADEN_MIDSHIPS_COMMENTS || "",
    wlDistanceLadenLast: genInfo?.WL_DISTANCE_LADEN_LAST || "",
    wlDistanceLadenLastComments: genInfo?.WL_DISTANCE_LADEN_LAST_COMMENTS || "",
    distKeelNo1: genInfo?.DIST_KEEL_NO1 || "",
    distKeelNo1Comments: genInfo?.DIST_KEEL_NO1_COMMENTS || "",
    distKeelMidships: genInfo?.DIST_KEEL_MIDSHIPS || "",
    distKeelMidshipsComments: genInfo?.DIST_KEEL_MIDSHIPS_COMMENTS || "",
    distKeelLast: genInfo?.DIST_KEEL_LAST || "",
    distKeelLastComments: genInfo?.DIST_KEEL_LAST_COMMENTS || "",
    hatchesSingleOrDoubleSeals: genInfo?.HATCHES_SINGLE_OR_DOUBLE_SEALS || "",
    grossTonnageGRT: genInfo?.GROSS_TONNAGE_GRT || "",
    netRegisteredTonnageNRT: genInfo?.NET_REGISTERED_TONNAGE_NRT || "",
    suezCanalGrossTonnageSCGT: genInfo?.SUEZ_CANAL_GROSS_TONNAGE_SCGT || "",
    suezCanalGrossTonnageSCGTComments: genInfo?.SUEZ_CANAL_GROSS_TONNAGE_SCGT_COMMENTS || "",
    suezCanalNetTonnageSCNT: genInfo?.SUEZ_CANAL_NET_TONNAGE_SCNT || "",
    suezCanalNetTonnageSCNTComments: genInfo?.SUEZ_CANAL_NET_TONNAGE_SCNT_COMMENTS || "",
    panamaCanalNetTonnagePCNT: genInfo?.PANAMA_CANAL_NET_TONNAGE_PCNT || "",
    summerDeadweight: genInfo?.SUMMER_DEADWEIGHT || "",
    summerDraft: genInfo?.SUMMER_DRAFT || "",
    tpcAtSummerDraft: genInfo?.TPC_AT_SUMMER_DRAFT || "",
    winterDeadweight: genInfo?.WINTER_DEADWEIGHT || "",
    winterDraft: genInfo?.WINTER_DRAFT || "",
    tpcImmersionWinterDraft: genInfo?.TPC_IMMERSION_WINTER_DRAFT || "",
    winterNorthAtlanticDeadweight: genInfo?.WINTER_NORTH_ATLANTIC_DEADWEIGHT || "",
    winterNorthAtlanticDraft: genInfo?.WINTER_NORTH_ATLANTIC_DRAFT || "",
    tpcImmersionWinterNorthAtlanticDraft: genInfo?.TPC_IMMERSION_WINTER_NORTH_ATLANTIC_DRAFT || "",
    freshWaterDeadweight: genInfo?.FRESH_WATER_DEADWEIGHT || "",
    freshWaterDraft: genInfo?.FRESH_WATER_DRAFT || "",
    tpcImmersionFreshWaterDraft: genInfo?.TPC_IMMERSION_FRESH_WATER_DRAFT || "",
    tropicalDeadweight: genInfo?.TROPICAL_DEADWEIGHT || "",
    tropicalDraft: genInfo?.TROPICAL_DRAFT || "",
    tpcImmersionTropicalDraft: genInfo?.TPC_IMMERSION_TROPICAL_DRAFT || "",
    tropicalFreshWaterDeadweight: genInfo?.TROPICAL_FRESH_WATER_DEADWEIGHT || "",
    tropicalFreshWaterDraft: genInfo?.TROPICAL_FRESH_WATER_DRAFT || "",
    tpcImmersionTropicalFreshDraft: genInfo?.TPC_IMMERSION_TROPICAL_FRESH_DRAFT || "",
    normalBallastConditionDeadweight: genInfo?.NORMAL_BALLAST_CONDITION_DEADWEIGHT || "",
    normalBallastConditionDraft: genInfo?.NORMAL_BALLAST_CONDITION_DRAFT || "",
    normalBallastConditionDraftComments: genInfo?.NORMAL_BALLAST_CONDITION_DRAFT_COMMENTS || "",
    tpcImmersionNormalBallastDraft: genInfo?.TPC_IMMERSION_NORMAL_BALLAST_DRAFT || "",
    lightshipDeadweight: genInfo?.LIGHTSHIP_DEADWEIGHT || "",
    lightshipDraft: genInfo?.LIGHTSHIP_DRAFT || "",
    tpcImmersionLightshipDraft: genInfo?.TPC_IMMERSION_LIGHTSHIP_DRAFT || "",
    lakesDeadweight: genInfo?.LAKES_DEADWEIGHT || "",
    lakesDraft: genInfo?.LAKES_DRAFT || "",
    tpcImmersionLakesDraft: genInfo?.TPC_IMMERSION_LAKES_DRAFT || "",
    freshWaterAllowanceAtSummerDraft: genInfo?.FRESH_WATER_ALLOWANCE_AT_SUMMER_DRAFT || "",
    underKeelClearanceGuidelines: genInfo?.UNDER_KEEL_CLEARANCE_GUIDELINES || "",
    underKeelClearanceGuidelinesComments: genInfo?.UNDER_KEEL_CLEARANCE_GUIDELINES_COMMENTS || "",
    fittedForPanama: genInfo?.FITTED_FOR_PANAMA || "",
    fittedForPanamaComments: genInfo?.FITTED_FOR_PANAMA_COMMENTS || "",
    panamaDeadweightAllTold: genInfo?.PANAMA_DEADWEIGHT_ALL_TOLD || "",
    isPanamaDwAffectedBilgeTurnRadius: genInfo?.IS_PANAMA_DW_AFFECTED_BILGE_TURN_RADIUS || "",
    isPanamaDwAffectedBilgeTurnRadiusComments: genInfo?.IS_PANAMA_DW_AFFECTED_BILGE_TURN_RADIUS_COMMENTS || "",
    fittedForSuez: genInfo?.FITTED_FOR_SUEZ || "",
    fittedForSuezComments: genInfo?.FITTED_FOR_SUEZ_COMMENTS || "",
    fittedForStLawrence: genInfo?.FITTED_FOR_ST_LAWRENCE || "",
    fittedForStLawrenceComments: genInfo?.FITTED_FOR_ST_LAWRENCE_COMMENTS || "",
    stLawrenceDeadweightAllTold: genInfo?.ST_LAWRENCE_DEADWEIGHT_ALL_TOLD || "",
    stLawrenceDeadweightAllToldComments: genInfo?.ST_LAWRENCE_DEADWEIGHT_ALL_TOLD_COMMENTS || "",
    pollutionGroundingCollision12Months: genInfo?.POLLUTION_GROUNDING_COLLISION_12_MONTHS || "",
    useAnswerFromCargoHistory: genInfo?.USE_ANSWER_FROM_CARGO_HISTORY || "",
    lastThreeVoyages: genInfo?.LAST_THREE_VOYAGES || "",
    internationalShipSecurityCertificate: genInfo?.INTERNATIONAL_SHIP_SECURITY_CERTIFICATE || "",

    // Incidents
    detailsIfIncidentPast12Months:
      incidents?.map((incident) => ({
        id: incident?.ID || "",
        incidentId: incident?.INCIDENT_ID || "",
        date: incident?.DATE || null,
        type: incident?.TYPE || "",
        location: incident?.LOCATION || "",
      })) || [],
  } as Bq1GeneralInformationM;
}
